import { createSlice } from '@reduxjs/toolkit';
import { profiles } from '../../data/mockData';

const initialState = {
  currentProfile: null,
  profiles: profiles,
  isAuthenticated: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    selectProfile: (state, action) => {
      state.currentProfile = action.payload;
      state.isAuthenticated = true;
    },
    logout: (state) => {
      state.currentProfile = null;
      state.isAuthenticated = false;
    },
    addToWatchlist: (state, action) => {
      if (state.currentProfile) {
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile && !profile.watchlist.includes(action.payload)) {
          profile.watchlist.push(action.payload);
          state.currentProfile.watchlist.push(action.payload);
        }
      }
    },
    removeFromWatchlist: (state, action) => {
      if (state.currentProfile) {
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile) {
          profile.watchlist = profile.watchlist.filter(id => id !== action.payload);
          state.currentProfile.watchlist = state.currentProfile.watchlist.filter(id => id !== action.payload);
        }
      }
    },
    updateContinueWatching: (state, action) => {
      if (state.currentProfile) {
        const { contentId, progress } = action.payload;
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile) {
          const existingIndex = profile.continueWatching.findIndex(item => item.contentId === contentId);
          const watchItem = {
            contentId,
            progress,
            lastWatched: new Date().toISOString().split('T')[0]
          };

          if (existingIndex !== -1) {
            profile.continueWatching[existingIndex] = watchItem;
            state.currentProfile.continueWatching[existingIndex] = watchItem;
          } else {
            profile.continueWatching.push(watchItem);
            state.currentProfile.continueWatching.push(watchItem);
          }
        }
      }
    },
    createProfile: (state, action) => {
      const newProfile = {
        id: Date.now(),
        ...action.payload,
        watchlist: [],
        continueWatching: [],
        customSections: []
      };
      state.profiles.push(newProfile);
    },
    editProfile: (state, action) => {
      const { id, ...updates } = action.payload;
      const profile = state.profiles.find(p => p.id === id);
      if (profile) {
        Object.assign(profile, updates);
        if (state.currentProfile && state.currentProfile.id === id) {
          Object.assign(state.currentProfile, updates);
        }
      }
    },
    addCustomSection: (state, action) => {
      if (state.currentProfile) {
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile) {
          const newSection = {
            id: `custom-${Date.now()}`,
            ...action.payload
          };
          profile.customSections = profile.customSections || [];
          profile.customSections.push(newSection);
          state.currentProfile.customSections = state.currentProfile.customSections || [];
          state.currentProfile.customSections.push(newSection);

          // Save to localStorage
          localStorage.setItem(
            `customSections_${profile.id}`,
            JSON.stringify(profile.customSections)
          );
        }
      }
    },
    updateCustomSection: (state, action) => {
      if (state.currentProfile) {
        const { sectionId, updates } = action.payload;
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile) {
          const sectionIndex = profile.customSections.findIndex(s => s.id === sectionId);
          if (sectionIndex !== -1) {
            profile.customSections[sectionIndex] = {
              ...profile.customSections[sectionIndex],
              ...updates
            };
            state.currentProfile.customSections[sectionIndex] = {
              ...state.currentProfile.customSections[sectionIndex],
              ...updates
            };

            // Save to localStorage
            localStorage.setItem(
              `customSections_${profile.id}`,
              JSON.stringify(profile.customSections)
            );
          }
        }
      }
    },
    deleteCustomSection: (state, action) => {
      if (state.currentProfile) {
        const sectionId = action.payload;
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile) {
          profile.customSections = profile.customSections.filter(s => s.id !== sectionId);
          state.currentProfile.customSections = state.currentProfile.customSections.filter(s => s.id !== sectionId);

          // Save to localStorage
          localStorage.setItem(
            `customSections_${profile.id}`,
            JSON.stringify(profile.customSections)
          );
        }
      }
    },
    reorderSections: (state, action) => {
      if (state.currentProfile) {
        const { sectionId, direction } = action.payload; // direction: 'up' or 'down'
        const profile = state.profiles.find(p => p.id === state.currentProfile.id);
        if (profile) {
          const sectionIndex = profile.customSections.findIndex(s => s.id === sectionId);
          if (sectionIndex !== -1) {
            const newIndex = direction === 'up' ? sectionIndex - 1 : sectionIndex + 1;
            if (newIndex >= 0 && newIndex < profile.customSections.length) {
              // Swap positions
              const temp = profile.customSections[sectionIndex];
              profile.customSections[sectionIndex] = profile.customSections[newIndex];
              profile.customSections[newIndex] = temp;

              // Update currentProfile as well
              state.currentProfile.customSections = [...profile.customSections];

              // Save to localStorage
              localStorage.setItem(
                `customSections_${profile.id}`,
                JSON.stringify(profile.customSections)
              );
            }
          }
        }
      }
    },
    loadCustomSections: (state, action) => {
      if (state.currentProfile) {
        const profileId = action.payload;
        const profile = state.profiles.find(p => p.id === profileId);
        if (profile) {
          const saved = localStorage.getItem(`customSections_${profileId}`);
          if (saved) {
            try {
              const sections = JSON.parse(saved);
              profile.customSections = sections;
              state.currentProfile.customSections = sections;
            } catch (error) {
              console.error('Failed to load custom sections from localStorage:', error);
            }
          }
        }
      }
    }
  },
});

export const {
  selectProfile,
  logout,
  addToWatchlist,
  removeFromWatchlist,
  updateContinueWatching,
  createProfile,
  editProfile,
  addCustomSection,
  updateCustomSection,
  deleteCustomSection,
  reorderSections,
  loadCustomSections
} = userSlice.actions;

export default userSlice.reducer;